// Imports
import React, { useContext, useEffect, useState } from "react";
import { db, storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { addDoc, collection, doc, Timestamp, updateDoc } from "firebase/firestore";
//Import Context
import { AppContext } from "../../../contexts/AppContext";
import { AuthContext } from "../../../contexts/AuthContext";
//Import Components
import RequestPreview from "../PurchaseForm/RequestPreview";
//Imports Helpers
import { extractNumber, formatCurrency, formatDate, formatDateJS, formatRut, getSubventionNames } from "../../../shared/helpers";
import { safeFolioGeneration } from "../../../shared/folioGenerator";
import { validateForm } from "./formValidation";
// Imports Icons
import arrowSide from '../../../assets/icons/arrowSide.svg';
import purchasingReqIcon from '../../../assets/icons/purchasingReqWhite.svg';
import add from '../../../assets/icons/add.svg';
import upload from '../../../assets/icons/upload.svg';
import trash from '../../../assets/icons/delete.svg';
import warning from '../../../assets/icons/infoWhite.svg';
// Import CSS
import '../PurchaseForm/purchaseRequestForm.css';

function FeeContractForm({ activity, establishment, pme, project, onClose, onRequestSubmitted }) {
    const { requestsList } = useContext(AppContext);
    const { userData } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        folio: '',
        supereducAccount: '',
        responsibleName: userData?.permissions === 'admin' ? '' : userData?.displayName,
        responsiblePhone: userData?.phoneNumber ? userData.phoneNumber : '',
        responsiblePosition: userData?.permissions === 'admin' ? '' : userData?.department,
        observations: '',
        technicalJustification: '',
        professionalName: '',
        professionalRut: '',
        hoursPerMonth: '',
        monthlyAmount: 0,
        numberOfMonths: '',
        totalAmount: 0,
        startDate: activity.startDate,
        endDate: activity.endDate,
        attachments: {
            backups: [],
            officialDocs: []
        }
    });
    const [errors, setErrors] = useState({
        responsibleInfo: '',
        contract: '',
        officialDocs: ''
    });
    const [incompleteItems, setIncompleteItems] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [exceedsPmeBalance, setExceedsPmeBalance] = useState(false);
    const [requestStatus, setRequestStatus] = useState({ confirmation: false, error: false });

    const today = new Date();
    const optionsDate = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    };

    useEffect(() => {
        const contenedor = document.querySelector('.purchase-request-container'); // o tu clase principal scrollable
        if (contenedor) contenedor.scrollTop = 0;
    }, []);

    const calculateTotalValue = (quantity, unitValue) => {
        const quantityNum = extractNumber(quantity)
        return quantityNum && unitValue ? quantityNum * unitValue : '';
    };

    const calculateTotalAmount = () => {
        const monthlyAmount = parseInt(formData.monthlyAmount) || 0;
        const numberOfMonths = parseInt(formData.numberOfMonths) || 0;
        return monthlyAmount * numberOfMonths;
    };
    const pmeBalance = activity.effectiveExpense;

    const addItem = () => {
        setFormData(prevState => {
            const newId = prevState.shoppingItems.length + 1;
            return {
                ...prevState,
                shoppingItems: [...prevState.shoppingItems, { id: newId, detail: '', quantity: '', unitValue: 0, totalValue: 0 }]
            };
        });
    };

    const removeItem = (e, id) => {
        e.preventDefault();
        setFormData(prevState => {
            // Filtramos los items y reindexamos
            const updatedItems = prevState.shoppingItems
                .filter(item => item.id !== id)
                .map((item, index) => ({
                    ...item,
                    id: index + 1 // Reasignamos IDs secuencialmente
                }));

            return {
                ...prevState,
                shoppingItems: updatedItems
            };
        });
    };

    useEffect(() => {
        const generateFolio = () => {
            if (pme?.id) {
                const newFolio = safeFolioGeneration(pme.id, requestsList);
                setFormData(prevState => ({ ...prevState, folio: newFolio }));
            }
        };

        generateFolio();
    }, [pme?.id, requestsList]);


    const isFormValid = () => {
        const { isValid } = validateForm(formData, errors, incompleteItems);
        return isValid && !exceedsPmeBalance;
    };

    useEffect(() => {
        if (showPreview) return;

        const totalAmount = calculateTotalAmount();
        const exceeds = totalAmount > pmeBalance;
        setExceedsPmeBalance(exceeds);

        if (exceeds) {
            setErrors(prev => ({
                ...prev,
                contract: `El monto total del contrato (${formatCurrency(totalAmount)}) excede el saldo PME disponible (${formatCurrency(pmeBalance)})`
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.contract;
                return newErrors;
            });
        }
    }, [formData.monthlyAmount, formData.numberOfMonths, pmeBalance]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newFormData = {
            ...formData,
            [name]: value
        };
        setFormData(newFormData);

        const { newErrors, newIncompleteItems } = validateForm(newFormData, errors, incompleteItems);
        setErrors(newErrors);
        setIncompleteItems(newIncompleteItems);
    };

    const handleContractChange = (e) => {
        const { name, value } = e.target;
        const newValue = name === 'monthlyAmount'
            ? parseInt(value.replace(/[^\d]/g, ''), 10) || 0
            : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        // Ejecutar validación y actualizar errores
        const { newErrors } = validateForm(formData, errors);
        setErrors(newErrors);
    };

    const handleRutChange = (e) => {
        const { value } = e.target;
        const formattedRut = formatRut(value.toUpperCase()); // Convertir K a mayúscula

        // Actualizar el estado usando el handler existente
        handleContractChange({
            target: {
                name: 'professionalRut',
                value: formattedRut
            }
        });
    };

    const handleFileUpload = async (e, type) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        const allowedTypes = [
            'application/pdf',                                                          // PDF
            'application/msword',                                                       // DOC
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // DOCX
            'application/vnd.ms-excel',                                                 // XLS
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',        // XLSX
            'application/vnd.oasis.opendocument.spreadsheet',                           // ODS
            'text/csv',                                                                 // CSV
            'image/jpeg',                                                               // JPG/JPEG
            'image/jpg',                                                                // JPG alternativo
            'image/png'                                                                 // PNG
        ];

        // Filtrar archivos no válidos
        const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
        if (invalidFiles.length > 0) {
            alert(`Algunos archivos no son de un formato válido. Por favor, use PDF, DOC, DOCX, XLS, XLSX, CSV, JPG o PNG.`);
            // Continuar con los archivos válidos
        }

        const validFiles = files.filter(file => allowedTypes.includes(file.type));
        if (validFiles.length === 0) return; // No hay archivos válidos para subir

        const uploadPromises = validFiles.map(async (file) => {
            try {
                const storageRef = ref(storage, `requests/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                return { fileName: file.name, fileUrl: downloadURL };
            } catch (error) {
                console.error("Error al subir el archivo: ", error);
                return null
            }
        });

        try {
            const uploadedFiles = (await Promise.all(uploadPromises)).filter(file => file !== null);
            if (uploadedFiles.length === 0) {
                alert("No se pudo subir ningún archivo. Por favor, inténtelo de nuevo.");
                return;
            }
            // Actualizar el estado con los nuevos archivos
            const newFormData = {
                ...formData,
                attachments: {
                    ...formData.attachments,
                    [type]: [...formData.attachments[type], ...uploadedFiles]
                }
            };
            setFormData(newFormData);

            // Validar el formulario actualizado
            const { newErrors, newIncompleteItems } = validateForm(newFormData, errors, incompleteItems);
            setErrors(newErrors);
            setIncompleteItems(newIncompleteItems);

            // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
            e.target.value = '';
        } catch (error) {
            console.error("Error al procesar archivos:", error);
            alert("Ocurrió un error al subir los archivos. Por favor, inténtelo de nuevo.");
        }
    };

    const handleCreateRequest = (e) => {
        e.preventDefault();

        if (isFormValid() && !exceedsPmeBalance) {
            setShowPreview(true);
        }
    };

    const handleSubmit = async (selectedDepartment, folio, requestAmount) => {
        const totalAmount = calculateTotalAmount();

        const requestData = {
            idEstablishment: establishment.id,
            idPME: pme.id,
            idActivity: activity.id,
            idProject: project,
            typeOfRequest: 'feeContract',
            requestDetails: {
                ...formData,
                folio: folio,
                requestAmount: totalAmount,
                state: 'pending',
                created: Timestamp.now(),
                sentTo: selectedDepartment.value
            },
            history: [
                {
                    backups: formData.attachments,
                    timestamp: Timestamp.now(),
                    responsibleName: formData.responsibleName,
                    role: (userData.permissions && userData.permissions === 'admin') ? formData.responsiblePosition : userData.department,
                    action: 'Solicitud Contrato de Honorarios creada',
                    details: 'Solicitud creada y enviada para revisión',
                    amount: totalAmount,
                    sentTo: selectedDepartment.value,

                }
            ]
        };

        try {
            // Obtiene referencia a la colección de solicitudes
            const requestsCollection = collection(db, 'requests');
            // Añade la solicitud a Firestore
            const docRef = await addDoc(requestsCollection, requestData);
            console.log("Solicitud añadida con ID: ", docRef.id);
            // Actualizar el accruedExpense de la actividad en el PME
            const pmeDocRef = doc(db, 'pme', pme.id);
            const updatedActivities = pme.activities.map(act => {
                if (act.id === activity.id) {
                    return {
                        ...act,
                        accruedExpense: act.accruedExpense + totalAmount,
                        effectiveExpense: act.totalSubsidyAmount - (act.accruedExpense + totalAmount) - act.accruedBalance
                    };
                }
                return act;
            });
            await updateDoc(pmeDocRef, { activities: updatedActivities });
            // Maneja el éxito (ej., muestra un mensaje de éxito, cierra el formulario)
            // Obtener la actividad actualizada
            const updatedActivity = updatedActivities.find(act => act.id === activity.id);
            onClose();
            setShowPreview(false);
            setRequestStatus({ confirmation: true, error: false });
            onRequestSubmitted(true, updatedActivity);
        } catch (error) {
            console.error("Error al añadir la solicitud: ", error);
            setRequestStatus({ confirmation: false, error: true });
        }
    };

    return (
        <section className={`request-form-container flex ${showPreview ? 'no-scroll' : ''}`}>
            <section className='info-establishment flex'>
                <h3>{establishment.nameEstablishment}</h3>
                <img src={arrowSide} className='icon' alt='Icono de flecha' />
                <p>{`Plan de Mejoramiento Educativo ${pme?.yearPME}`}</p>
            </section>
            <section className='form-container flex'>
                <p className='text-bold'>Cobertura Curricular</p>
                <form className='form flex' onSubmit={handleCreateRequest}>
                    <table className='data'>
                        <thead>
                            <tr>
                                <th>Establecimiento</th>
                                <th>RBD</th>
                                <th>Fecha</th>
                                <th>Folio</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td data-label='Establecimiento'>{establishment.nameEstablishment}</td>
                                <td data-label='RBD'>{establishment.rbd}</td>
                                <td data-label='Fecha'>{today.toLocaleDateString('es-Es', optionsDate)}</td>
                                <td data-label='Folio'>{formData.folio}</td>
                            </tr>
                        </tbody>
                    </table>
                    <DescriptionOfTheAction
                        activity={activity}
                        pme={pme}
                        formData={formData}
                        handleInputChange={handleInputChange}
                    />
                    <PmeAmounts
                        activity={activity}
                    />
                    <ContractDetails
                        formData={formData}
                        handleContractChange={handleContractChange}
                        calculateTotalAmount={calculateTotalAmount}
                        activity={activity}
                        handleRutChange={handleRutChange}
                    />
                    {errors?.contract && <p className="error-message">{errors.contract}</p>}
                    <InformationInput
                        formData={formData}
                        handleInputChange={handleInputChange}
                        isAdmin={userData?.permissions === 'admin'}
                    />
                    {errors.responsibleInfo && <p className="error-message">{errors.responsibleInfo}</p>}
                    <FileInput
                        formData={formData}
                        handleFileUpload={handleFileUpload}
                        setFormData={setFormData}
                    />
                    {errors.officialDocs && <p className="error-message">{errors.officialDocs}</p>}
                    <section className='button-box flex'>
                        <button
                            type='submit'
                            className={`button flex ${!isFormValid() ? 'disabled' : ''}`}
                            disabled={!isFormValid()}
                        >
                            <img
                                src={purchasingReqIcon}
                                className='icon'
                                alt='Icono para crear solicitud de contrato de servicios.'
                            />
                            <p>Crear Solicitud de Contrato de Servicios</p>
                        </button>
                        <p className='cancel-button' onClick={onClose}>Cancelar</p>
                        {!isFormValid() && <span className='tooltip-message flex'><img src={warning} alt='Icono de Advertencia botón deshabilitado' /><p>Faltan o hay errores en algunos campos. Revísalos.</p></span>}
                    </section>
                </form>
                {showPreview &&
                    <RequestPreview
                        formData={formData}
                        activity={activity}
                        onSubmit={handleSubmit}
                        onCancel={() => setShowPreview(false)}
                        requestAmount={calculateTotalAmount()}
                        requestStatus={requestStatus}
                        establishment={establishment.id}
                        pme={pme.id}
                        typeOfRequest='feeContract'
                    />
                }
            </section>
        </section>
    )
};

export default FeeContractForm;

const DescriptionOfTheAction = ({ activity, pme, formData, handleInputChange }) => {
    return (
        <article className='info-box flex'>
            <span className='item flex first-item'>
                <p className='title flex'>Año de Gasto</p>
                <p className='result flex'>{pme?.yearPME}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Tipo de Subvención</p>
                <p className='result flex'>{`${getSubventionNames(activity.subsidies)} ${pme?.yearPME}`}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Nombre de la Acción</p>
                <p className='result flex'>{activity.nameOfAction}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Dimensión</p>
                <p className='result flex'>{activity.dimension}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Subdimensión</p>
                <p className='result flex'>{activity.subdimension}</p>
            </span>
            <span className='item flex last-item'>
                <p className='title flex'>Cuenta SUPEREDUC</p>
                <span className='result flex'>
                    <label className='label-hidden'>Cuenta SUPEREDUC</label>
                    <input
                        type='text'
                        name='supereducAccount'
                        className='input'
                        value={formData.supereducAccount || ''}
                        onChange={handleInputChange}
                        placeholder='Ingresa la cuenta SUPEREDUC'
                    />
                </span>
            </span>
        </article>
    );
};

const PmeAmounts = ({ activity }) => {
    return (
        <table className='pme-amounts data border'>
            <thead>
                <tr>
                    <th>Monto Acción PME</th>
                    <th>Monto PME Utilizado</th>
                    <th>Saldo PME</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td data-label='Monto Acción PME'>{formatCurrency(activity.totalSubsidyAmount)}</td>
                    <td data-label='Monto PME Utilizado'>{formatCurrency(activity.accruedBalance + activity.accruedExpense)}</td>
                    <td data-label='Saldo PME'>{formatCurrency(activity.effectiveExpense)}</td>
                </tr>
            </tbody>
        </table>
    );
};

const ContractDetails = ({ formData, handleContractChange, calculateTotalAmount, activity, handleRutChange }) => {
    return (
        <article className='info-box flex'>
            <span className='item flex first-item'>
                <p className='title flex'>Nombre Profesional (*)</p>
                <span className='result flex'>
                    <label className='label-hidden'>Nombre Profesional</label>
                    <input
                        type='text'
                        name='professionalName'
                        className='input'
                        value={formData.professionalName}
                        onChange={handleContractChange}
                        placeholder='Ingresa el nombre del profesional'
                    />
                </span>
            </span>
            <span className='item flex'>
                <p className='title flex'>Rut Profesional (*)</p>
                <span className='result flex'>
                    <label className='label-hidden'>Rut Profesional</label>
                    <input
                        type='text'
                        name='professionalRut'
                        className='input'
                        value={formData.professionalRut}
                        onChange={handleRutChange}
                        placeholder='Ingresa el rut del profesional EJ: 12.345.678-9'
                        maxLength={12}
                    />
                </span>
            </span>
            <span className='item flex'>
                <p className='title flex'>Nº de Horas Mes (*)</p>
                <span className='result flex'>
                    <label className='label-hidden'>Nº de Horas Mes</label>
                    <input
                        type='number'
                        name='hoursPerMonth'
                        className='input'
                        value={formData.hoursPerMonth}
                        onChange={handleContractChange}
                        placeholder='Ingresa nº de horas por mes'
                        onWheel={(e) => e.target.blur()}
                        min="0"
                    />
                </span>
            </span>
            <span className='item flex'>
                <p className='title flex'>Monto de Pago Mes (*)</p>
                <span className='result flex'>
                    <label className='label-hidden'>Monto de Pago Mes</label>
                    <input
                        type='text'
                        name='monthlyAmount'
                        className='input'
                        value={formatCurrency(formData.monthlyAmount)}
                        onChange={handleContractChange}
                        placeholder='Ingresa monto de pago por mes'
                    />
                </span>
            </span>
            <span className='item flex'>
                <p className='title flex'>Nº de Meses (*)</p>
                <span className='result flex'>
                    <label className='label-hidden'>Nº de Meses</label>
                    <input
                        type='number'
                        name='numberOfMonths'
                        className='input'
                        value={formData.numberOfMonths}
                        onChange={handleContractChange}
                        placeholder='Ingresa nº de meses'
                        onWheel={(e) => e.target.blur()}
                        min="0"
                    />
                </span>
            </span>
            <span className='item flex'>
                <p className='title flex'>Monto Total Contrato</p>
                <p className='result'>{formatCurrency(calculateTotalAmount())}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Fecha Inicio</p>
                <p className='result'>{(activity && activity.startDate) ? formatDateJS(activity.startDate) : ''}</p>
            </span>
            <span className='item flex last-item'>
                <p className='title flex'>Fecha Termino</p>
                <p className='result'>{(activity && activity.endDate) ? formatDateJS(activity.endDate) : ''}</p>
            </span>
        </article>
    );
};

const InformationInput = ({ formData, handleInputChange, isAdmin }) => {
    return (
        <article className='info-box info-box2 flex'>
            <span className='item flex first-item'>
                <p className='title flex'>Responsable de la acción</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <label htmlFor='responsibleName' className='label-input'>Nombre (*)</label>
                        {isAdmin ? (
                            <input
                                type='text'
                                name='responsibleName'
                                className='input'
                                placeholder='Ingrese nombre del responsable'
                                value={formData.responsibleName}
                                onChange={handleInputChange}
                            />
                        ) : (
                            <p>{formData.responsibleName}</p>
                        )}

                    </span>
                    <span className='input-container flex'>
                        <label htmlFor='phone' className='label-input'>Teléfono</label>
                        <input
                            type='text'
                            name='responsiblePhone'
                            className='input'
                            placeholder='Ingrese teléfono'
                            value={formData.responsiblePhone}
                            onChange={handleInputChange}
                        />
                    </span>
                    <span className='input-container flex'>
                        <label htmlFor='responsiblePosition' className='label-input'>Cargo (*)</label>
                        {isAdmin ? (
                            <input
                                type='text'
                                name='responsiblePosition'
                                className='input'
                                placeholder='Ingrese cargo del responsable'
                                value={formData.responsiblePosition}
                                onChange={handleInputChange}
                            />
                        ) : (
                            <p>{formData.responsiblePosition}</p>
                        )}

                    </span>
                </div>
            </span>
            <span className='item middle-item flex'>
                <p className='title flex'>Observaciones</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <label htmlFor='observations' className='label-hidden'>Observaciones</label>
                        <textarea
                            type='text'
                            name='observations'
                            className='input flex'
                            placeholder='Ingrese sus observaciones'
                            value={formData.observations}
                            onChange={handleInputChange}
                        />
                    </span>
                </div>
            </span>
            <span className='item flex last-item'>
                <p className='title flex'>Justificación técnica pedagógica</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <label htmlFor='technicalJustification' className='label-hidden'>Justificación técnica pedagógica</label>
                        <textarea
                            type='text'
                            name='technicalJustification'
                            className='input flex'
                            placeholder='Ingrese justificación'
                            value={formData.technicalJustification}
                            onChange={handleInputChange}
                        />
                    </span>
                </div>
            </span>
        </article>
    );
};

const FileInput = ({ formData, handleFileUpload, setFormData }) => {
    const handleDeleteFile = (e, type, fileToDelete) => {
        e.preventDefault();
        e.stopPropagation();

        const updatedFiles = formData.attachments[type].filter(
            file => file.fileName !== fileToDelete.fileName ||
                file.fileUrl !== fileToDelete.fileUrl
        );

        setFormData(prevState => ({
            ...prevState,
            attachments: {
                ...prevState.attachments,
                [type]: updatedFiles
            }
        }));
    };
    return (
        <article className='info-box info-box3 flex'>
            <span className='item flex'>
                <p className='title flex'>Adjuntar Oficio (*)</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <div className='input flex'>
                            {(formData.attachments.officialDocs.length > 0) && (
                                <ul className='file-list flex'>
                                    {formData.attachments.officialDocs.map((file, index) => (
                                        <li key={index} className='file-item' title={file.fileName}>
                                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className='file-name'>
                                                {file.fileName}
                                            </a>
                                            <button
                                                type='button'
                                                className='delete-file-btn'
                                                onClick={(e) => handleDeleteFile(e, 'officialDocs', file)}
                                                title='Eliminar archivo'
                                            >
                                                <img src={trash} className='icon' alt='Eliminar archivo' />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <label className='button-add-backup flex' htmlFor='file-upload'>
                                <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                                <p>Subir Oficio</p>
                            </label>
                            <input
                                id="file-upload"
                                type="file"
                                onChange={(e) => handleFileUpload(e, 'officialDocs')}
                                multiple
                                style={{ display: 'none' }} // Oculta el input
                            />
                        </div>
                    </span>
                </div>
            </span>
            <span className='item flex'>
                <p className='title flex'>Adjuntar Respaldos</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <div className='input flex'>
                            {(formData.attachments.backups.length > 0) && (
                                <ul className='file-list flex'>
                                    {formData.attachments.backups.map((file, index) => (
                                        <li key={index} className='file-item'>
                                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className='file-name'>
                                                {file.fileName}
                                            </a>
                                            <button
                                                type='button'
                                                className='delete-file-btn'
                                                onClick={(e) => handleDeleteFile(e, 'backups', file)}
                                                title='Eliminar archivo'
                                            >
                                                <img src={trash} className='icon' alt='Eliminar archivo' />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <label className='button-add-backup flex' htmlFor='backups-upload'>
                                <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                                <p>Subir Respaldo</p>
                            </label>
                            <input
                                id="backups-upload"
                                type="file"
                                onChange={(e) => handleFileUpload(e, 'backups')}
                                multiple
                                style={{ display: 'none' }} // Oculta el input
                            />
                        </div>
                    </span>
                </div>
            </span>
            {/* <p className="file-formats-info">Formatos permitidos: PDF, DOC, XLS, XLSX, CSV, JPG, PNG</p> */}
        </article>
    );
};